import type { ReactNode } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BookingEmbed } from "@/components/site/BookingEmbed";
import { HealthyContactEmbed } from "@/components/site/HealthyContactEmbed";
import { useLang } from "@/i18n/LangContext";
import {
  getContactFormUrl,
  isBookableAction,
  type BookingAction,
} from "@/data/certificateBookingActions";
import { cn } from "@/lib/utils";

type BookingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  action: BookingAction | null | undefined;
  /** Internal certificate slug for post-booking form redirect */
  slug?: string | null;
  /** Stable key when switching certificates */
  contentKey?: string;
  children?: ReactNode;
  className?: string;
};

export const BookingDialog = ({
  open,
  onOpenChange,
  title,
  action,
  slug = null,
  contentKey,
  children,
  className,
}: BookingDialogProps) => {
  const { lang, t } = useLang();
  const contactFormUrl = action ? getContactFormUrl(action) : null;
  const key = contentKey ?? title;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-[hsl(162_52%_8%/0.55)] backdrop-blur-sm"
        className={cn(
          "max-h-[88vh] w-[min(72rem,calc(100vw-1rem))] max-w-none gap-5 overflow-y-auto overflow-x-hidden",
          "rounded-2xl border border-white/70 bg-white/95 p-5 shadow-[0_24px_60px_-20px_hsl(162_52%_8%/0.45)]",
          "supports-[backdrop-filter]:bg-white/90 sm:rounded-3xl sm:p-6 lg:p-8",
          className,
        )}
      >
        <DialogTitle className="pr-10 font-display text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
          {title}
        </DialogTitle>

        {children ??
          (action && isBookableAction(action) ? (
            <BookingEmbed key={key} action={action} title={title} slug={slug} />
          ) : contactFormUrl ? (
            <div className="space-y-3">
              <p className="hidden text-sm text-muted-foreground sm:block">
                {lang === "sv"
                  ? "För detta intyg hjälper vi dig via kontaktformuläret."
                  : "For this certificate, we help you via the contact form."}
              </p>
              <HealthyContactEmbed
                key={key}
                src={contactFormUrl}
                title={title || t.contact.formTitle}
                className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-card"
                iframeClassName="h-[70vh] min-h-[520px] w-full border-0"
              />
            </div>
          ) : null)}
      </DialogContent>
    </Dialog>
  );
};
